// src/ui/ProjectList.tsx

import React, { useEffect, useState } from 'react';
import { listProjectsFromDB, deleteProjectFromDB, loadProjectFromDB } from '../state/projectStore';
import { useProjectStore } from '../state/projectStore';
import type { ProjectState } from '../types';

interface ProjectListProps {
  onNewProject: () => void;
  onOpenProject: () => void;
}

export const ProjectList: React.FC<ProjectListProps> = ({ onNewProject, onOpenProject }) => {
  const [projects, setProjects] = useState<Pick<ProjectState, 'id' | 'name' | 'createdAt' | 'updatedAt'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectList = await listProjectsFromDB();
      setProjects(projectList.sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadProjects();
  }, []);
  
  const handleDelete = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await deleteProjectFromDB(projectId);
      await loadProjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    }
  };
  
  const handleOpen = async (projectId: string) => {
    try {
      const project = await loadProjectFromDB(projectId);
      if (project) {
        useProjectStore.setState(project);
        onOpenProject();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open project');
    }
  };
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  if (loading) {
    return (
      <div className="project-list loading">
        <p>Loading projects...</p>
      </div>
    );
  }
  
  return (
    <div className="project-list">
      <header>
        <h1>PCB Assembly Helper</h1>
        <button onClick={onNewProject} className="btn-primary">
          New Project
        </button>
      </header>
      
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      
      {projects.length === 0 ? (
        <div className="empty-state">
          <p>No projects yet.</p>
          <p>Create a new project to get started with your PCB assembly.</p>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project) => (
            <div
              key={project.id}
              className="project-card"
              onClick={() => handleOpen(project.id)}
            >
              <div className="project-header">
                <h3>{project.name}</h3>
                <button
                  className="delete-btn"
                  onClick={(e) => handleDelete(project.id, e)}
                  title="Delete project"
                >
                  ×
                </button>
              </div>
              
              <div className="project-meta">
                <span>Created: {formatDate(project.createdAt)}</span>
                <span>Updated: {formatDate(project.updatedAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
